/**
 * useProfile — minimal hook for updating the current user's profile.
 *
 * The v2 backend exposes `PUT /auth/me` which accepts a `UserUpdate` payload.
 * The current `users` model is intentionally minimal (auth identity only):
 * the only writable field today is `name`. Additional fields from the
 * mockup (pronouns, title, department, location, bio) are not yet
 * supported by the backend — Profile.tsx renders them as TODO placeholders.
 *
 * This hook lives in the surface (talent profile) because the substrate
 * shipped without a `useUpdateProfile`; per task spec it's an allowed
 * exception.
 */

import { useMutation } from '@tanstack/react-query'
import api from '../lib/api'
import { useAuth } from '../context/AuthContext'
import {
  useImpersonationOptional,
  type ImpersonationTarget,
} from '../context/ImpersonationContext'
import type { User, UserUpdate } from '../types'

export function useUpdateProfile() {
  const { applyUser } = useAuth()
  const impersonation = useImpersonationOptional()
  return useMutation({
    mutationFn: async (body: UserUpdate) => {
      const res = await api.put<User>('/auth/me', body)
      return res.data
    },
    // The user object lives in AuthContext's `useState`, not in React Query, so
    // there is no cache to invalidate. The PUT returns the saved user: adopt it
    // at once. Refetching /auth/me afterwards left a window in which the page
    // still showed the old value, and a second click built on that stale value
    // and overwrote the first. Under View-as the PUT went to the target, whose
    // snapshot is what the page reads, so that is what gets the update.
    onSuccess: (saved) => {
      if (impersonation?.target)
        impersonation.updateTarget(saved as unknown as Partial<ImpersonationTarget>)
      else applyUser(saved)
    },
  })
}
